import React, { useState, useEffect, useCallback } from 'react';
import './MonitoringPage.css';

const MONITORING_ACCESS_CODE = '201C';

const MONITORING_TOOLS = [
  { key: 'portainer',   label: 'Portainer',   port: 9443, protocol: 'https', path: '',  description: 'Container management and Docker dashboard.' },
  { key: 'uptime-kuma', label: 'Uptime Kuma', port: 3001, protocol: 'http',  path: '',  description: 'Uptime monitoring and alerting.' },
  { key: 'dozzle',      label: 'Dozzle',      port: 9999, protocol: 'http',  path: '',  description: 'Real-time Docker container log viewer.' },
  { key: 'pgadmin',     label: 'pgAdmin',     port: 5050, protocol: 'http',  path: '',  description: 'PostgreSQL database management.' },
];

function getToolUrl(tool) {
  const host = (window._env_ && window._env_.REACT_APP_MONITORING_HOST)
    || window.location.hostname;
  return `${tool.protocol}://${host}:${tool.port}${tool.path}`;
}

function MonitoringPage() {
  const [verified, setVerified] = useState(
    () => localStorage.getItem('monitoringAccessVerified') === 'true'
  );
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const [activeTool, setActiveTool] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setCodeError('Please enter the access code.');
      return;
    }
    if (code.trim().toUpperCase() === MONITORING_ACCESS_CODE) {
      localStorage.setItem('monitoringAccessVerified', 'true');
      setVerified(true);
    } else {
      setCodeError('Invalid access code. Please try again.');
      setCode('');
    }
  };

  const openInIframe = (tool) => {
    setActiveTool(tool);
    setIframeError(false);
    setFullscreen(false);
  };

  const openInNewTab = (tool) => {
    window.open(getToolUrl(tool), '_blank', 'noopener,noreferrer');
  };

  const closeIframe = () => {
    setActiveTool(null);
    setFullscreen(false);
    setIframeError(false);
  };

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && fullscreen) {
      setFullscreen(false);
    }
  }, [fullscreen]);

  useEffect(() => {
    if (fullscreen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [fullscreen, handleEscape]);

  /* ── Password gate ─────────────────────────────────────────────── */
  if (!verified) {
    return (
      <div className="user-home-layout__page monitoring-page">
        <div className="user-home-layout__page-header">
          <h1 className="user-home-layout__page-title">Monitoring</h1>
          <p className="user-home-layout__page-subtitle">
            Access restricted. Enter the monitoring access code to continue.
          </p>
        </div>

        <div className="user-home-layout__card">
          <div className="monitoring-gate">
            <div className="monitoring-gate__icon">🔒</div>
            <h2 className="monitoring-gate__title">Access Code Required</h2>
            <p className="monitoring-gate__text">
              Enter the monitoring access code to view infrastructure tools.
            </p>
            <form onSubmit={handleCodeSubmit} className="monitoring-gate__form">
              {codeError && <div className="monitoring-gate__error">{codeError}</div>}
              <input
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value); if (codeError) setCodeError(''); }}
                placeholder="Enter access code"
                className="monitoring-gate__input"
                autoFocus
                autoComplete="off"
              />
              <button type="submit" className="monitoring-gate__btn">
                Unlock
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main dashboard ────────────────────────────────────────────── */
  const activeUrl = activeTool ? getToolUrl(activeTool) : null;

  return (
    <div className="user-home-layout__page monitoring-page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">Monitoring</h1>
        <p className="user-home-layout__page-subtitle">
          Infrastructure monitoring and management tools
        </p>
      </div>

      <div className="user-home-layout__card">
        <h2 className="user-home-layout__section-title">Tools</h2>
        <div className="monitoring-tools-grid">
          {MONITORING_TOOLS.map((tool) => (
            <div
              key={tool.key}
              className={`monitoring-tool-card${activeTool?.key === tool.key ? ' monitoring-tool-card--active' : ''}`}
            >
              <div className="monitoring-tool-card__body">
                <h3 className="monitoring-tool-card__title">{tool.label}</h3>
                <p className="monitoring-tool-card__desc">{tool.description}</p>
                <span className="monitoring-tool-card__url">{getToolUrl(tool)}</span>
              </div>
              <div className="monitoring-tool-card__actions">
                <button
                  className="monitoring-tool-card__btn monitoring-tool-card__btn--primary"
                  onClick={() => openInIframe(tool)}
                  title="Open in embedded viewer"
                >
                  Open
                </button>
                <button
                  className="monitoring-tool-card__btn monitoring-tool-card__btn--secondary"
                  onClick={() => openInNewTab(tool)}
                  title="Open in new browser tab"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  New Tab
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Iframe viewer ──────────────────────────────────────────── */}
      {activeTool && (
        <div className="user-home-layout__card" style={{ marginTop: 16 }}>
          <div className="monitoring-iframe-toolbar">
            <span className="monitoring-iframe-toolbar__title">{activeTool.label}</span>
            <div className="monitoring-iframe-toolbar__actions">
              <button
                className="monitoring-iframe-toolbar__btn"
                onClick={() => openInNewTab(activeTool)}
                title="Open in new tab"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                New Tab
              </button>
              <button
                className="monitoring-iframe-toolbar__btn"
                onClick={() => setFullscreen(true)}
                title="Fullscreen"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <polyline points="9 21 3 21 3 15"></polyline>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
                Fullscreen
              </button>
              <button
                className="monitoring-iframe-toolbar__btn monitoring-iframe-toolbar__btn--close"
                onClick={closeIframe}
                title="Close viewer"
              >
                ✕
              </button>
            </div>
          </div>

          {iframeError ? (
            <div className="monitoring-iframe-fallback">
              <p>This tool could not be loaded in the embedded viewer (it may block iframe embedding).</p>
              <button
                className="monitoring-iframe-fallback__btn"
                onClick={() => openInNewTab(activeTool)}
              >
                Open {activeTool.label} in a new tab instead
              </button>
            </div>
          ) : (
            <iframe
              key={activeTool.key}
              src={activeUrl}
              title={activeTool.label}
              className="monitoring-iframe"
              onError={() => setIframeError(true)}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
            />
          )}
        </div>
      )}

      {/* ── Fullscreen overlay ─────────────────────────────────────── */}
      {fullscreen && activeTool && (
        <div className="monitoring-fullscreen-overlay">
          <div className="monitoring-fullscreen-toolbar">
            <span className="monitoring-fullscreen-toolbar__title">{activeTool.label}</span>
            <div className="monitoring-fullscreen-toolbar__actions">
              <button
                className="monitoring-fullscreen-toolbar__btn"
                onClick={() => openInNewTab(activeTool)}
                title="Open in new tab"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                New Tab
              </button>
              <button
                className="monitoring-fullscreen-toolbar__btn"
                onClick={() => setFullscreen(false)}
                title="Exit fullscreen (Esc)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="14" y1="10" x2="21" y2="3"></line>
                  <line x1="3" y1="21" x2="10" y2="14"></line>
                </svg>
                Exit Fullscreen
              </button>
            </div>
          </div>
          <iframe
            key={`fs-${activeTool.key}`}
            src={activeUrl}
            title={`${activeTool.label} (fullscreen)`}
            className="monitoring-fullscreen-iframe"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
          />
        </div>
      )}
    </div>
  );
}

export default MonitoringPage;
